import { useState, useEffect } from "react";
import InputMask from "react-input-mask";
import { Dentist, CreateDentistData } from "@/services/dentist-service";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { onlyNumbers, formatCRO } from "@/utils/formatters";

interface CreateEditDentistDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dentist: Dentist | null;
  onSave: (data: CreateDentistData, id?: string) => Promise<void>;
}

export function CreateEditDentistDialog({
  open,
  onOpenChange,
  dentist,
  onSave,
}: CreateEditDentistDialogProps) {
  const [name, setName] = useState("");
  const [cpf, setCpf] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [email, setEmail] = useState("");
  const [cellPhone, setCellPhone] = useState("");
  const [cro, setCro] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (dentist) {
      setName(dentist.name);
      setCpf(dentist.cpf);
      setBirthDate(dentist.birth_date);
      setEmail(dentist.email);
      setCellPhone(dentist.cellphone);
      setCro(formatCRO(dentist.cro));
    } else {
      clearForm();
    }
  }, [dentist, open]);

  const clearForm = () => {
    setName("");
    setCpf("");
    setBirthDate("");
    setEmail("");
    setCellPhone("");
    setCro("");
    setErrors({});
  };

  const croRegex = /^[A-Z]{2}-\d{4,6}$/;

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!name.trim()) newErrors.name = "Nome é obrigatório";

    const cleanCpf = onlyNumbers(cpf);
    if (cleanCpf.length !== 11) newErrors.cpf = "CPF inválido";

    if (!birthDate) newErrors.birthDate = "Data de nascimento é obrigatória";

    if (!email.trim()) newErrors.email = "Email é obrigatório";

    const cleanCellPhone = onlyNumbers(cellPhone);
    if (cleanCellPhone.length < 10 || cleanCellPhone.length > 11)
      newErrors.cellPhone = "Telefone inválido";

    if (!cro.trim()) newErrors.cro = "CRO é obrigatório";
    else if (!croRegex.test(cro.trim().toUpperCase()))
      newErrors.cro = "CRO no formato UF-12345. Exemplo: SP-12345";

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      setTimeout(() => document.getElementById(Object.keys(newErrors)[0])?.focus(), 0);
    }
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    const data: CreateDentistData = {
      name,
      cpf: onlyNumbers(cpf),
      birth_date: birthDate,
      email,
      cellphone: onlyNumbers(cellPhone),
      cro: cro.trim().toUpperCase(),
    };

    try {
      await onSave(data, dentist?.id);
      onOpenChange(false);
    } catch (error) {
      // Erros de salvamento ficam a cargo do pai
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="border-b pb-2">
          <DialogTitle className="text-sm font-semibold uppercase tracking-wide">
            {dentist ? "Editar Profissional" : "Novo Profissional"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="space-y-1">
            <Label className="filter-label" htmlFor="name">Nome *</Label>
            <Input className="filter-input" id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome completo" aria-invalid={!!errors.name} />
            {errors.name && <p className="text-red-600 text-xs">{errors.name}</p>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="filter-label" htmlFor="cpf">CPF *</Label>
              <InputMask mask="999.999.999-99" value={cpf} onChange={(e) => setCpf(e.target.value)}>
                {(inputProps: any) => <Input className="filter-input" {...inputProps} id="cpf" aria-invalid={!!errors.cpf} />}
              </InputMask>
              {errors.cpf && <p className="text-red-600 text-xs">{errors.cpf}</p>}
            </div>
            <div className="space-y-1">
              <Label className="filter-label" htmlFor="birthDate">Data de Nascimento *</Label>
              <Input className="filter-input" id="birthDate" type="date" value={birthDate} onChange={(e) => setBirthDate(e.target.value)} aria-invalid={!!errors.birthDate} />
              {errors.birthDate && <p className="text-red-600 text-xs">{errors.birthDate}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="filter-label" htmlFor="email">Email *</Label>
              <Input className="filter-input" id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@exemplo.com" aria-invalid={!!errors.email} />
              {errors.email && <p className="text-red-600 text-xs">{errors.email}</p>}
            </div>
            <div className="space-y-1">
              <Label className="filter-label" htmlFor="cellPhone">Telefone *</Label>
              <InputMask mask="(99) 99999-9999" value={cellPhone} onChange={(e) => setCellPhone(e.target.value)}>
                {(inputProps: any) => <Input className="filter-input" {...inputProps} id="cellPhone" placeholder="(00) 00000-0000" aria-invalid={!!errors.cellPhone} />}
              </InputMask>
              {errors.cellPhone && <p className="text-red-600 text-xs">{errors.cellPhone}</p>}
            </div>
          </div>

          <div className="space-y-1">
            <Label className="filter-label" htmlFor="cro">CRO *</Label>
            <Input
              className="filter-input"
              id="cro"
              value={cro}
              onChange={e => {
                let v = e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
                if (v.length > 2 && !v.includes('-')) v = `${v.slice(0,2)}-${v.slice(2)}`;
                setCro(v);
              }}
              placeholder="UF-12345"
              aria-invalid={!!errors.cro}
              maxLength={9}
            />
            {errors.cro && <p className="text-red-600 text-xs">{errors.cro}</p>}
          </div>
        </div>

        <div className="flex items-center justify-between pt-3 border-t gap-2">
          {Object.keys(errors).length > 0 && (
            <p className="text-red-600 text-xs">Corrija os campos marcados em vermelho.</p>
          )}
          <div className="flex gap-2 ml-auto">
            <Button variant="outline" size="sm" className="btn-header" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button size="sm" className="btn-header" onClick={handleSave}>
              {dentist ? "Salvar Alterações" : "Criar Profissional"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
